import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, Circle, ChevronDown, ChevronRight, RefreshCw, Bell, Upload, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Color based on % complete
function rowColor(pct) {
  if (pct >= 100) return "bg-emerald-50 border-emerald-200";
  if (pct > 0) return "bg-amber-50 border-amber-200";
  return "bg-white border-slate-100";
}

function jobStatusBadge(status, t) {
  if (status === "completed") return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">{t("contractorBoard.complete")}</Badge>;
  if (status === "in_progress") return <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">{t("contractorBoard.inProgress")}</Badge>;
  return <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]">{t("contractorBoard.notStarted")}</Badge>;
}

export default function ContractorProgressBoardPage({ user, onLogout }) {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobIdx, setSelectedJobIdx] = useState(0);
  const [toggling, setToggling] = useState({});
  const [collapsedFloors, setCollapsedFloors] = useState({});
  const [milestones, setMilestones] = useState([]);
  const [reportDialog, setReportDialog] = useState({ open: false, milestone: null });
  const [reportForm, setReportForm] = useState({ title: "", description: "", attachments: [] });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [milestoneMaterials, setMilestoneMaterials] = useState(null);

  const loadMilestones = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/contractor/billing-milestones`);
      setMilestones(data.milestones || []);
    } catch {}
  }, []);

  const loadBoard = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/contractor/progress-board`);
      setJobs(data.jobs || []);
    } catch {
      toast.error("Failed to load progress board");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBoard();
    loadMilestones();
  }, [loadBoard, loadMilestones]);

  const toggleSubtask = async (jobId, unitId, subtaskId, currentValue) => {
    const key = `${jobId}_${unitId}_${subtaskId}`;
    if (toggling[key]) return;
    setToggling(t => ({ ...t, [key]: true }));

    // Optimistic update
    setJobs(prev => prev.map(job => {
      if (job.job_id !== jobId) return job;
      const totalWeight = job.subtasks.reduce((s, st) => s + (st.weight || 0), 0);
      const useWeights = totalWeight > 0;
      const newUnits = job.units.map(unit => {
        if (unit.unit_id !== unitId) return unit;
        const newSubtasks = { ...unit.subtasks, [subtaskId]: { completed: !currentValue } };
        let completed_count = 0, completed_weight = 0;
        for (const st of job.subtasks) {
          if (newSubtasks[st.id]?.completed) {
            completed_count++;
            completed_weight += (st.weight || 0);
          }
        }
        const progress_percent = useWeights
          ? Math.round(completed_weight / totalWeight * 100)
          : job.subtasks.length > 0 ? Math.round(completed_count / job.subtasks.length * 100) : 0;
        return { ...unit, subtasks: newSubtasks, completed_count, progress_percent };
      });
      const completed_cells = newUnits.reduce((s, u) => s + u.completed_count, 0);
      const overall_progress = newUnits.length > 0
        ? Math.round(newUnits.reduce((s, u) => s + u.progress_percent, 0) / newUnits.length)
        : 0;
      const status = overall_progress >= 100 ? "completed" : overall_progress > 0 ? "in_progress" : "not_started";
      return { ...job, units: newUnits, completed_cells, overall_progress, status };
    }));

    try {
      await axios.put(`${API}/project-progress/jobs/${jobId}/unit-progress/${unitId}/${subtaskId}`, {
        completed: !currentValue
      });
    } catch (err) {
      toast.error("Failed to update — refreshing");
      loadBoard();
    } finally {
      setToggling(prev => { const n = { ...prev }; delete n[key]; return n; });
      // Re-check milestones after every toggle
      loadMilestones();
    }
  };

  const openReportDialog = async (milestone) => {
    setReportForm({ title: milestone.auto_title || "", description: "", attachments: [] });
    setReportDialog({ open: true, milestone });
    setMilestoneMaterials(null);
    // Fetch material quantities for this job, filtered by milestone scope
    try {
      const { data } = await axios.get(`${API}/job-materials/${milestone.job_id}`);
      let items = data.items || [];
      if (items.length > 0) {
        // Filter by floor/building when milestone is by_floor
        if (milestone.type === "by_floor" && milestone.milestone_key) {
          const parts = milestone.milestone_key.split(":floor:");
          if (parts.length === 2) {
            const bldgFloor = parts[1];
            const sepIdx = bldgFloor.lastIndexOf("_");
            const buildingId = bldgFloor.substring(0, sepIdx);
            const floorNum = parseInt(bldgFloor.substring(sepIdx + 1));
            items = items.filter(it => it.building_id === buildingId && it.floor === floorNum);
          }
        }
        if (items.length > 0) {
          const total = items.reduce((s, it) => s + (it.quantity || 0), 0);
          const uom = items[0]?.unit_of_measure || "m\u00B2";
          setMilestoneMaterials({ total: Math.round(total * 100) / 100, uom, count: items.length });
        }
      }
    } catch {}
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingFiles(true);
    try {
      const uploaded = await Promise.all(files.map(async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        return { url: res.data.url, filename: res.data.filename || file.name };
      }));
      setReportForm(f => ({ ...f, attachments: [...f.attachments, ...uploaded] }));
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch { toast.error("Upload failed"); }
    finally { setUploadingFiles(false); }
  };

  const submitReport = async () => {
    if (!reportForm.title || !reportForm.description) {
      toast.error("Please fill in title and description");
      return;
    }
    const m = reportDialog.milestone;
    setSubmitting(true);
    try {
      // Get the job's project_id from the board data
      const job = jobs.find(j => j.job_id === m.job_id);
      await axios.post(`${API}/contractor/progress-reports`, {
        project_id: job?.project_id || null,
        task_id: m.job_id,
        title: reportForm.title,
        description: reportForm.description,
        progress_percentage: job?.overall_progress || 0,
        attachments: reportForm.attachments.map(a => a.url),
        milestone_key: m.milestone_key,
        milestone_type: m.type,
        milestone_label: m.label,
        apartment_keys: m.apartment_keys || null
      });
      toast.success(t("contractorBoard.reportSubmitted"));
      setReportDialog({ open: false, milestone: null });
      setReportForm({ title: "", description: "", attachments: [] });
      loadMilestones();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit report");
    } finally { setSubmitting(false); }
  };

  const toggleFloor = (jobId, floorKey) => {
    const key = `${jobId}_${floorKey}`;
    setCollapsedFloors(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-5 h-5 animate-spin text-slate-400 mr-2" />
          <span className="text-sm text-slate-500">{t("contractorBoard.loading")}</span>
        </div>
      </Layout>
    );
  }

  if (!jobs.length) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="space-y-6">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">{t("contractorBoard.title")}</h1>
            <p className="text-slate-500 text-sm mt-1">{t("contractorBoard.subtitle")}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
            <Circle className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-500">{t("contractorBoard.noJobs")}</p>
            <p className="text-sm text-slate-400 mt-1">Your project tasks will appear here once assigned by the project manager.</p>
          </div>
        </div>
      </Layout>
    );
  }

  const job = jobs[selectedJobIdx] || jobs[0];

  // Group units by floor then building
  const grouped = {};
  (job?.units || []).forEach(unit => {
    const key = `${unit.building_name}___Floor ${unit.floor}`;
    if (!grouped[key]) grouped[key] = { building: unit.building_name, floor: unit.floor, units: [] };
    grouped[key].units.push(unit);
  });
  const floorGroups = Object.entries(grouped).sort(([, a], [, b]) =>
    a.building.localeCompare(b.building) || a.floor - b.floor
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">{t("contractorBoard.title")}</h1>
            <p className="text-slate-500 text-sm mt-1">{t("contractorBoard.subtitle")}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { loadBoard(); loadMilestones(); }} className="rounded-sm">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> {t("contractorBoard.refreshBoard")}
          </Button>
        </div>

        {/* Milestone Banners */}
        {milestones.length > 0 && (
          <div className="space-y-2">
            {milestones.map((m, i) => (
              <div key={i} className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-sm px-4 py-3">
                <div className="flex items-center gap-3">
                  <Bell className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">{m.label}</p>
                    <p className="text-xs text-emerald-600">{m.summary}</p>
                  </div>
                </div>
                <Button size="sm" onClick={() => openReportDialog(m)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs ml-4 flex-shrink-0">
                  Submit for Engineer Approval
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Job Tabs */}
        {jobs.length > 1 && (
          <div className="flex gap-2 flex-wrap">
            {jobs.map((j, idx) => (
              <button
                key={j.job_id}
                onClick={() => setSelectedJobIdx(idx)}
                className={`px-4 py-2 rounded-sm text-sm font-medium border transition-colors ${
                  idx === selectedJobIdx
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {j.job_name}
              </button>
            ))}
          </div>
        )}

        {/* Job Summary Card */}
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-display text-lg font-bold uppercase text-slate-900">{job.job_name}</h2>
                {jobStatusBadge(job.status, t)}
              </div>
              <p className="text-sm text-slate-500">{job.project_name}</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900">{job.overall_progress}%</p>
              <p className="text-xs text-slate-400">{job.completed_cells} / {job.total_cells}</p>
            </div>
          </div>
          {/* Progress bar */}
          <div className="w-full bg-slate-100 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${
                job.overall_progress >= 100 ? "bg-emerald-500" :
                job.overall_progress > 0 ? "bg-amber-400" : "bg-slate-200"
              }`}
              style={{ width: `${job.overall_progress}%` }}
            />
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-400 inline-block" /> Done</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-slate-200 inline-block" /> Not done</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-50 border border-amber-200 inline-block" /> Apartment partially done</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-50 border border-emerald-200 inline-block" /> Apartment fully done</span>
        </div>

        {/* No subtasks warning */}
        {job.subtasks.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-sm text-amber-700">
            No work stages defined for this job yet. Ask the project engineer to add subtasks to <strong>{job.job_name}</strong> in Project Progress.
          </div>
        )}

        {/* Floor groups */}
        {floorGroups.map(([floorKey, group]) => {
          const collapseKey = `${job.job_id}_${floorKey}`;
          const isCollapsed = collapsedFloors[collapseKey];
          const floorDone = group.units.filter(u => u.progress_percent >= 100).length;
          const floorTotal = group.units.length;

          return (
            <div key={floorKey} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              {/* Floor Header */}
              <button
                onClick={() => toggleFloor(job.job_id, floorKey)}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  {isCollapsed ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  <span className="font-semibold text-slate-700 text-sm">
                    {group.building && <span className="text-slate-400 mr-1">{group.building} —</span>}
                    {t("contractorBoard.floor")} {group.floor}
                  </span>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  floorDone === floorTotal ? "bg-emerald-100 text-emerald-700" :
                  floorDone > 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"
                }`}>
                  {floorDone === floorTotal ? t("contractorBoard.allComplete") : `${floorDone}/${floorTotal}`}
                </span>
              </button>

              {/* Units Table */}
              {!isCollapsed && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider w-32 sticky left-0 bg-white">
                          Apartment
                        </th>
                        {job.subtasks.map(st => (
                          <th key={st.id} className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center min-w-[110px]">
                            {st.name}
                          </th>
                        ))}
                        <th className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center w-20">
                          Done
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.units.map(unit => (
                        <tr key={unit.unit_id} className={`border-b last:border-0 ${rowColor(unit.progress_percent)}`}>
                          {/* Apartment code */}
                          <td className="px-4 py-2.5 font-medium text-slate-800 sticky left-0 bg-inherit">
                            {unit.unit_code}
                          </td>
                          {/* Subtask cells */}
                          {job.subtasks.map(st => {
                            const cellKey = `${job.job_id}_${unit.unit_id}_${st.id}`;
                            const done = unit.subtasks[st.id]?.completed || false;
                            const isLoading = toggling[cellKey];
                            return (
                              <td key={st.id} className="px-3 py-2 text-center">
                                <button
                                  onClick={() => toggleSubtask(job.job_id, unit.unit_id, st.id, done)}
                                  disabled={isLoading}
                                  data-testid={`subtask-toggle-${unit.unit_id}-${st.id}`}
                                  className={`w-8 h-8 mx-auto flex items-center justify-center rounded-full transition-all ${
                                    isLoading ? "opacity-50 cursor-wait" :
                                    done ? "bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm" :
                                    "bg-slate-100 hover:bg-slate-200 text-slate-400"
                                  }`}
                                  title={done ? `${st.name}: Done — click to undo` : `Mark ${st.name} as done`}
                                >
                                  {done
                                    ? <CheckCircle2 className="w-4 h-4" />
                                    : <Circle className="w-4 h-4" />
                                  }
                                </button>
                              </td>
                            );
                          })}
                          {/* Progress % */}
                          <td className="px-3 py-2 text-center">
                            <span className={`text-xs font-bold ${
                              unit.progress_percent >= 100 ? "text-emerald-600" :
                              unit.progress_percent > 0 ? "text-amber-600" : "text-slate-400"
                            }`}>
                              {unit.progress_percent}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Report Submit Dialog */}
      <Dialog open={reportDialog.open} onOpenChange={(o) => setReportDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-slate-900 flex items-center gap-2">
              <Bell className="w-4 h-4 text-emerald-600" /> {t("contractorBoard.submitReport")}
            </DialogTitle>
          </DialogHeader>
          {reportDialog.milestone && (
            <div className="space-y-4 py-2">
              {/* Milestone summary */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
                <p className="text-sm font-semibold text-emerald-800">{reportDialog.milestone.label}</p>
                <p className="text-xs text-emerald-600 mt-0.5">{reportDialog.milestone.summary}</p>
              </div>

              {/* Material quantities info */}
              {milestoneMaterials && (
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-3" data-testid="milestone-materials-info">
                  <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-1">{t("contractorBoard.materialQuantities")}</p>
                  <p className="text-sm font-bold text-blue-900">{milestoneMaterials.total} {milestoneMaterials.uom}</p>
                  <p className="text-[11px] text-blue-500 mt-0.5">This data will be included in the report for engineer verification</p>
                </div>
              )}

              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("contractorBoard.reportTitle")} *</Label>
                <input
                  className="mt-1 w-full border border-slate-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                  value={reportForm.title}
                  onChange={e => setReportForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g., Floor 1 Parquet Complete"
                />
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("contractorBoard.reportDescription")} *</Label>
                <Textarea
                  className="mt-1 rounded-sm text-sm"
                  rows={3}
                  value={reportForm.description}
                  onChange={e => setReportForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Describe the work completed, any notes for the engineer..."
                />
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t("contractorBoard.attachments")}</Label>
                <label className="mt-1 flex flex-col items-center justify-center w-full border-2 border-dashed border-slate-200 rounded-sm p-4 cursor-pointer hover:border-slate-300 transition-colors">
                  <Upload className="w-5 h-5 text-slate-400 mb-1" />
                  <span className="text-xs text-slate-500">{uploadingFiles ? t("contractorBoard.uploading") : t("common.upload")}</span>
                  <input type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={handleFileUpload} disabled={uploadingFiles} />
                </label>
                {reportForm.attachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {reportForm.attachments.map((a, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-slate-50 border border-slate-100 rounded px-2 py-1">
                        <span className="truncate text-slate-600">{a.filename}</span>
                        <button onClick={() => setReportForm(f => ({ ...f, attachments: f.attachments.filter((_, j) => j !== i) }))}
                          className="text-slate-400 hover:text-red-500 ml-2"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2 border-t">
                <Button variant="outline" onClick={() => setReportDialog(d => ({ ...d, open: false }))} className="flex-1 rounded-sm">
                  {t("common.cancel")}
                </Button>
                <Button onClick={submitReport} disabled={submitting} className="flex-1 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white">
                  {submitting ? t("contractorBoard.submitting") : t("contractorBoard.submitReport")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
